import React, { useRef, useState, useEffect, useCallback } from 'react';

export interface Point { x: number; y: number }
export interface Stroke { points: Point[]; color: string; width: number; tool: 'pencil' | 'pen' | 'highlighter' | 'eraser' }
export interface PageAnnotations { [pageNumber: number]: Stroke[] }

export type DrawTool = 'pencil' | 'pen' | 'highlighter' | 'eraser';
export interface DrawToolState { tool: DrawTool; color: string; brushSize: number }

interface SmartboardCanvasProps {
  page: number;
  annotations: PageAnnotations;
  onAnnotationsChange: (annotations: PageAnnotations) => void;
  width: number;
  height: number;
  /** When provided, tool state is controlled externally — no toolbar rendered */
  toolState?: DrawToolState;
}

const TOOL_WIDTHS: Record<DrawTool, number> = { pencil: 0.5, pen: 1, highlighter: 5, eraser: 5 };

const SmartboardCanvas: React.FC<SmartboardCanvasProps> = ({
  page, annotations, onAnnotationsChange, width, height, toolState,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const currentPointsRef = useRef<Point[]>([]);

  const toolRef = useRef<DrawTool>(toolState?.tool ?? 'pencil');
  const colorRef = useRef(toolState?.color ?? '#2b2b2b');
  const sizeRef = useRef(toolState?.brushSize ?? 4);

  // Keep refs in sync with external tool state
  if (toolState) {
    toolRef.current = toolState.tool;
    colorRef.current = toolState.color;
    sizeRef.current = toolState.brushSize;
  }

  const currentStrokes = annotations[page] || [];

  const drawStroke = useCallback((ctx: CanvasRenderingContext2D, stroke: Stroke) => {
    if (stroke.points.length < 2) return;
    ctx.save();
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = stroke.width;
    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else if (stroke.tool === 'highlighter') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = stroke.color;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = stroke.color;
    }
    ctx.beginPath();
    ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
    for (let i = 1; i < stroke.points.length; i++) {
      const prev = stroke.points[i - 1];
      const curr = stroke.points[i];
      ctx.quadraticCurveTo(prev.x, prev.y, (prev.x + curr.x) / 2, (prev.y + curr.y) / 2);
    }
    ctx.stroke();
    ctx.restore();
  }, []);

  const redrawAll = useCallback((strokes: Stroke[]) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokes.forEach((s) => drawStroke(ctx, s));
  }, [drawStroke]);

  useEffect(() => { redrawAll(currentStrokes); }, [currentStrokes, redrawAll, width, height]);

  const buildStroke = useCallback((points: Point[]): Stroke => ({
    points,
    color: toolRef.current === 'eraser' ? '#000' : colorRef.current,
    width: sizeRef.current * TOOL_WIDTHS[toolRef.current],
    tool: toolRef.current,
  }), []);

  const getPos = useCallback((e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    isDrawingRef.current = true;
    currentPointsRef.current = [getPos(e)];
  }, [getPos]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    currentPointsRef.current.push(getPos(e));
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    redrawAll(annotations[page] || []);
    drawStroke(ctx, buildStroke(currentPointsRef.current));
  }, [getPos, redrawAll, drawStroke, buildStroke, annotations, page]);

  const handlePointerUp = useCallback(() => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const points = currentPointsRef.current;
    currentPointsRef.current = [];
    if (points.length >= 2) {
      const newStroke = buildStroke(points);
      const newStrokes = [...(annotations[page] || []), newStroke];
      onAnnotationsChange({ ...annotations, [page]: newStrokes });
    }
  }, [buildStroke, annotations, page, onAnnotationsChange]);

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="absolute inset-0 z-10"
        style={{ width: '100%', height: '100%', cursor: toolRef.current === 'eraser' ? 'cell' : 'crosshair', touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
    </div>
  );
};

export default SmartboardCanvas;


// ── Standalone Drawing Toolbar (rendered once, outside the canvas loop) ──────

const PRESET_COLORS = ['#2b2b2b', '#3973bf', '#d40000', '#ffda09', '#59e56e', '#e5599b', '#ff8c00', '#9b59b6'];

const TOOL_ICONS: { tool: DrawTool; emoji: string; label: string }[] = [
  { tool: 'pencil', emoji: '✏️', label: 'Kalem' },
  { tool: 'pen', emoji: '🖊️', label: 'Tükenmez' },
  { tool: 'highlighter', emoji: '🖍️', label: 'Fosforlu' },
  { tool: 'eraser', emoji: '🧹', label: 'Silgi' },
];

interface DrawToolbarProps {
  toolState: DrawToolState;
  onToolStateChange: (s: DrawToolState) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onClear?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

export const DrawToolbar: React.FC<DrawToolbarProps> = ({
  toolState, onToolStateChange, onUndo, onRedo, onClear, canUndo, canRedo,
}) => {
  const [visible, setVisible] = useState(true);
  const [confirmClear, setConfirmClear] = useState(false);
  const { tool, color, brushSize } = toolState;

  const set = (patch: Partial<DrawToolState>) => onToolStateChange({ ...toolState, ...patch });

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setVisible(!visible)}
        className="fixed top-20 right-5 z-[80] w-[36px] h-[36px] rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 border border-gray-200"
        title={visible ? 'Araçları Gizle' : 'Araçları Göster'}
      >
        <svg width="20" height="16" viewBox="0 0 22 16" fill="none" stroke="#2b2b2b" strokeWidth="1.5">
          <path d="M1 8s4-7 10-7 10 7 10 7-4 7-10 7S1 8 1 8z" />
          <circle cx="11" cy="8" r="3" />
          {!visible && <line x1="2" y1="2" x2="20" y2="14" strokeWidth="2" />}
        </svg>
      </button>

      {visible && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] bg-white rounded-[16px] shadow-lg border border-gray-200 px-3 py-2 flex items-center gap-1 flex-wrap max-w-[95vw]">
          {/* Tools */}
          {TOOL_ICONS.map(({ tool: t, emoji, label }) => (
            <button key={t} onClick={() => set({ tool: t })}
              className={`w-[40px] h-[40px] rounded-[10px] flex items-center justify-center transition-colors ${tool === t ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              title={label}>
              <span className="text-[18px]">{emoji}</span>
            </button>
          ))}

          <div className="w-[1px] h-[28px] bg-gray-300 mx-1" />

          {/* Undo */}
          <button onClick={onUndo} disabled={!canUndo}
            className="w-[36px] h-[36px] flex items-center justify-center hover:bg-gray-100 rounded-[8px] disabled:opacity-30" title="Geri Al">
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" stroke="#666" strokeWidth="2"><path d="M6 1L1 6l5 5" /><path d="M1 6h12a4 4 0 010 8H8" /></svg>
          </button>

          {/* Redo */}
          <button onClick={onRedo} disabled={!canRedo}
            className="w-[36px] h-[36px] flex items-center justify-center hover:bg-gray-100 rounded-[8px] disabled:opacity-30" title="İleri Al">
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" stroke="#666" strokeWidth="2"><path d="M12 1l5 5-5 5" /><path d="M17 6H5a4 4 0 000 8h5" /></svg>
          </button>

          {/* Clear */}
          <button onClick={() => setConfirmClear(true)}
            className="w-[36px] h-[36px] flex items-center justify-center hover:bg-gray-100 rounded-[8px]" title="Hepsini Sil">
            <svg width="16" height="18" viewBox="0 0 16 18" fill="none" stroke="#666" strokeWidth="1.5"><path d="M1 4h14M5 4V2h6v2M3 4v12a1 1 0 001 1h8a1 1 0 001-1V4" /></svg>
          </button>

          <div className="w-[1px] h-[28px] bg-gray-300 mx-1" />

          {/* Color presets */}
          {PRESET_COLORS.map((c) => (
            <button key={c} onClick={() => set({ tool: tool === 'eraser' ? 'pencil' : tool, color: c })}
              className={`w-[28px] h-[28px] rounded-full flex-shrink-0 transition-transform ${color === c && tool !== 'eraser' ? 'ring-2 ring-offset-2 ring-[#3973bf] scale-110' : ''}`}
              style={{ background: c }} />
          ))}

          {/* Custom color */}
          <input type="color" value={color}
            onChange={(e) => set({ tool: tool === 'eraser' ? 'pencil' : tool, color: e.target.value })}
            className="w-[28px] h-[28px] rounded-full cursor-pointer border-0 p-0"
            title="Özel Renk"
            style={{ appearance: 'none', background: 'conic-gradient(red,yellow,lime,aqua,blue,magenta,red)', borderRadius: '50%' }}
          />

          <div className="w-[1px] h-[28px] bg-gray-300 mx-1" />

          {/* Size slider */}
          <div className="flex items-center gap-1">
            <div className="w-[6px] h-[6px] rounded-full bg-[#2b2b2b]" />
            <input type="range" min={1} max={30} value={brushSize}
              onChange={(e) => set({ brushSize: Number(e.target.value) })}
              className="w-[70px] accent-[#3973bf]" />
            <div className="rounded-full bg-[#2b2b2b] flex-shrink-0"
              style={{ width: Math.min(20, Math.max(4, brushSize)), height: Math.min(20, Math.max(4, brushSize)) }} />
          </div>
        </div>
      )}

      {/* Clear-all confirmation */}
      {confirmClear && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4"
          onClick={() => setConfirmClear(false)}>
          <div className="bg-white rounded-[16px] shadow-xl max-w-[380px] w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-[40px] h-[40px] rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <svg width="18" height="20" viewBox="0 0 16 18" fill="none" stroke="#e53e3e" strokeWidth="1.5"><path d="M1 4h14M5 4V2h6v2M3 4v12a1 1 0 001 1h8a1 1 0 001-1V4" /></svg>
              </div>
              <div className="flex-1">
                <h3 className="text-[16px] font-semibold text-[#2b2b2b]">Tüm çizimleri sil</h3>
                <p className="text-[14px] text-gray-500 mt-1">Bu işlem bütün çizimleri kaldıracak. Onaylıyor musunuz?</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmClear(false)}
                className="px-4 py-2 rounded-[10px] text-[14px] font-medium text-gray-600 hover:bg-gray-100">
                Vazgeç
              </button>
              <button onClick={() => { setConfirmClear(false); onClear?.(); }}
                className="px-4 py-2 rounded-[10px] text-[14px] font-medium text-white bg-[#e53e3e] hover:bg-[#d32f2f]">
                Hepsini Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
